import { useState, useEffect, useContext } from "react";
import {
    Table,
    Typography,
    Spin,
    Tag,
    Button,
    message,
    Modal,
    Input,
    Descriptions,
    Tabs,
    Divider,
    DatePicker,
} from "antd";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

export default function ResignationRequests() {
    const { authData, backUrl } = useContext(AuthContext);

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [approving, setApproving] = useState(false);

    const [modalVisible, setModalVisible] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [actionType, setActionType] = useState(null); // 'resignation', 'withdrawal', 'waiver'

    const [detailsModal, setDetailsModal] = useState(false);
    const [selectedDetails, setSelectedDetails] = useState(null);
    const [finalizeModalVisible, setFinalizeModalVisible] = useState(false);
    const [lastWorkingDay, setLastWorkingDay] = useState(null);
    const [dateOfLeaving, setDateOfLeaving] = useState(null);

    // 🔹 Fetch All Requests and Merge
    const fetchRequests = async () => {
        try {
            setLoading(true);

            // Fetch resignations
            const resignationsRes = await axios.get(
                `${backUrl}/api/payroll/resignations/my-queue/`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            // Fetch withdrawals
            const withdrawalsRes = await axios.get(
                `${backUrl}/api/payroll/withdrawal-tasks/`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            // Fetch waivers
            const waiversRes = await axios.get(
                `${backUrl}/api/payroll/waiver-tasks/`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            const resignations = resignationsRes.data?.results || resignationsRes.data || [];
            const withdrawals = withdrawalsRes.data?.results || withdrawalsRes.data || [];
            const waivers = waiversRes.data?.results || waiversRes.data || [];

            // Create a map of resignations by ID for easy lookup
            const resignationMap = new Map(
                resignations.map(r => [r.id, { ...r, withdrawal: null, waiver: null }])
            );

            // Attach withdrawals to their corresponding resignations by employee ID
            withdrawals.forEach(withdrawal => {
                const employeeId = withdrawal.withdrawal_request?.employee?.id;
                const matchingResignation = Array.from(resignationMap.values()).find(
                    r => r.employee?.id === employeeId
                );
                if (matchingResignation) {
                    resignationMap.get(matchingResignation.id).withdrawal = withdrawal;
                }
            });

            // Attach waivers to their corresponding resignations by employee ID
            waivers.forEach(waiver => {
                const employeeId = waiver.waiver_request?.employee?.id;
                const matchingResignation = Array.from(resignationMap.values()).find(
                    r => r.request?.employee?.id === employeeId
                );
                if (matchingResignation) {
                    resignationMap.get(matchingResignation.id).waiver = waiver;
                }
            });

            setRequests(Array.from(resignationMap.values()));
        } catch (err) {
            console.error(err);
            message.error("Failed to fetch requests");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    // 🔹 Open Approve Modal
    const openApproveModal = (record, type) => {
        setSelectedRequest(record);
        setActionType(type);
        setRemarks("");
        setModalVisible(true);
    };

    // 🔹 Handle Approval
    const handleApprove = async () => {
        if (!remarks.trim()) {
            toast.warning("Please enter remarks");
            return;
        }

        try {
            setApproving(true);
            let endpoint = "";

            if (actionType === "resignation") {
                endpoint = `${backUrl}/api/payroll/resignations/${selectedRequest.id}/approve/`;
            } else if (actionType === "withdrawal") {
                endpoint = `${backUrl}/api/payroll/withdrawal-requests/${selectedRequest.withdrawal.withdrawal_request.id}/approve/`;
            } else if (actionType === "waiver") {
                endpoint = `${backUrl}/api/payroll/waiver-requests/${selectedRequest.waiver.waiver_request.id}/approve/`;
            }

            await axios.post(
                endpoint,
                { action: "APPROVED", remarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            toast.success(`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} approved successfully`);
            setModalVisible(false);
            fetchRequests();
        } catch (err) {
            console.error(err);
            toast.error("Approval failed");
        } finally {
            setApproving(false);
        }
    };

    // 🔹 Open Finalize Modal
    const openFinalizeModal = (record) => {
        setSelectedRequest(record);
        setLastWorkingDay(record.requested_last_working_day ? dayjs(record.requested_last_working_day) : null);
        setDateOfLeaving(record.requested_last_working_day ? dayjs(record.requested_last_working_day) : null);
        setFinalizeModalVisible(true);
    };

    // 🔹 Handle Finalize
    const handleFinalize = async () => {
        if (!lastWorkingDay || !dateOfLeaving) {
            toast.warning("Please select both Last Working Day and Date of Leaving");
            return;
        }
        if (dateOfLeaving.isBefore(lastWorkingDay)) {
            toast.warning("Date of leaving cannot be before the last working day");
            return;
        }

        try {
            setApproving(true);
            const payload = {
                last_working_day: lastWorkingDay.format("YYYY-MM-DD"),
                date_of_leaving: dateOfLeaving.format("YYYY-MM-DD"),
            };

            await axios.post(
                `${backUrl}/api/payroll/resignations/${selectedRequest.id}/hr-finalize/`,
                payload,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            toast.success("Resignation dates finalized successfully");
            setFinalizeModalVisible(false);
            fetchRequests();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || err.response?.data?.non_field_errors?.[0] || "Finalization failed");
        } finally {
            setApproving(false);
        }
    };

    // 🔹 Open Details Modal
    const openDetailsModal = (record) => {
        setSelectedDetails(record);
        setDetailsModal(true);
    };

    // 🔹 Columns
    const isSuperuser = authData?.employee?.is_superuser;

    const columns = [
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            render: (name, record) => (
                <Button type="link" onClick={() => openDetailsModal(record)}>
                    {name}
                </Button>
            ),
        },
        {
            title: "Resigned Date",
            dataIndex: "resigned_date",
            render: (d) => d ? new Date(d).toLocaleDateString("en-GB") : "-",
        },
        {
            title: "Requested LWD",
            dataIndex: "requested_last_working_day",
            render: (d) => d ? new Date(d).toLocaleDateString("en-GB") : "-",
        },
        !isSuperuser && {
            title: "Resignation Status",
            dataIndex: "my_status",
            render: (s) => (
                <Tag color={s === "APPROVED" ? "green" : "orange"}>{s}</Tag>
            ),
        },
        {
            title: "Withdrawal",
            render: (_, record) => {
                if (!record.withdrawal) return <Tag>-</Tag>;
                const status = record.withdrawal.status;
                return (
                    <Tag color={status === "APPROVED" ? "green" : status === "PENDING" ? "orange" : "red"}>
                        {status}
                    </Tag>
                );
            },
        },
        {
            title: "Waiver",
            render: (_, record) => {
                if (!record.waiver) return <Tag>-</Tag>;
                const status = record.waiver.status;
                return (
                    <>
                        <Tag color={status === "APPROVED" ? "green" : status === "PENDING" ? "orange" : "red"}>
                            {status}
                        </Tag>
                        <p style={{ color: 'black' }}>{record?.waiver?.request?.reason}</p>
                    </>

                );
            },
        },
        {
            title: "Overall Status",
            dataIndex: "status",
            render: (s) => (
                <Tag color={s === "APPROVED" ? "green" : "orange"}>{s}</Tag>
            ),
        },
        {
            title: "Actions",
            render: (_, record) => {
                const isHrOrSuper = authData?.employee?.is_hr || authData?.employee?.is_superuser;
                return (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {/* Resignation Approval */}
                        {record.status !== "PENDING_FINAL_DATES" && record.status !== "COMPLETED" && record.status !== "REJECTED" && record.status !== "WITHDRAWN" && (
                            <Button
                                type="primary"
                                size="small"
                                disabled={record.my_status === "APPROVED"}
                                onClick={() => openApproveModal(record, "resignation")}
                                style={{ background: '#0e497b', color: 'white' }}
                            >
                                Approve Resignation
                            </Button>
                        )}

                        {/* Finalize Dates */}
                        {record.status === "PENDING_FINAL_DATES" && isHrOrSuper && (
                            <Button
                                type="primary"
                                size="small"
                                onClick={() => openFinalizeModal(record)}
                                style={{ background: '#28a745', color: 'white', borderColor: '#28a745' }}
                            >
                                Finalize Dates
                            </Button>
                        )}

                        {/* Withdrawal Approval */}
                        {record.withdrawal && (
                            <Button
                                type="primary"
                                size="small"
                                disabled={record.withdrawal.status === "APPROVED"}
                                onClick={() => openApproveModal(record, "withdrawal")}
                                style={{ background: '#0e497b', color: 'white' }}
                            >
                                Approve Withdrawal
                            </Button>
                        )}

                        {/* Waiver Approval */}
                        {record.waiver && (
                            <Button
                                type="primary"
                                size="small"
                                disabled={record.waiver.status === "APPROVED"}
                                onClick={() => openApproveModal(record, "waiver")}
                                style={{ background: '#0e497b', color: 'white' }}
                            >
                                Approve Waiver
                            </Button>
                        )}
                    </div>
                );
            },
        },
    ].filter(Boolean);

    return (
        <div style={{ maxWidth: "95%", margin: "40px auto" }}>
            <Title level={4}>Resignation Requests</Title>

            {loading ? (
                <Spin size="large" style={{ display: "block", margin: "40px auto" }} />
            ) : (
                <Table
                    dataSource={requests}
                    columns={columns}
                    rowKey="id"
                    bordered
                    pagination={{ pageSize: 15 }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: 'white' }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            )}

            {/* 🔹 Approve Modal */}
            <Modal
                title={`Approve ${actionType ? actionType.charAt(0).toUpperCase() + actionType.slice(1) : 'Request'}`}
                open={modalVisible}
                onOk={handleApprove}
                onCancel={() => setModalVisible(false)}
                confirmLoading={approving}
            >
                <Text strong>Remarks</Text>
                <TextArea
                    rows={4}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                />
            </Modal>

            {/* 🔹 Finalize Dates Modal */}
            <Modal
                title="Finalize Exit Dates"
                open={finalizeModalVisible}
                onOk={handleFinalize}
                onCancel={() => setFinalizeModalVisible(false)}
                confirmLoading={approving}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "15px", padding: "10px 0" }}>
                    <div>
                        <div style={{ marginBottom: "5px" }}><Text strong>Last Working Day</Text></div>
                        <DatePicker
                            style={{ width: "100%" }}
                            value={lastWorkingDay}
                            onChange={(date) => setLastWorkingDay(date)}
                            format="YYYY-MM-DD"
                        />
                    </div>
                    <div>
                        <div style={{ marginBottom: "5px" }}><Text strong>Date of Leaving</Text></div>
                        <DatePicker
                            style={{ width: "100%" }}
                            value={dateOfLeaving}
                            onChange={(date) => setDateOfLeaving(date)}
                            format="YYYY-MM-DD"
                        />
                    </div>
                </div>
            </Modal>

            {/* 🔹 Details Modal with Tabs */}
            <Modal
                title="Request Details"
                open={detailsModal}
                onCancel={() => setDetailsModal(false)}
                footer={null}
                width={900}
            >
                {selectedDetails && (
                    <Tabs
                        defaultActiveKey="resignation"
                        items={[
                            {
                                key: "resignation",
                                label: "Resignation",
                                children: (
                                    <>
                                        <Descriptions bordered column={2} style={{ marginBottom: 20 }}>
                                            <Descriptions.Item label="Employee">
                                                {selectedDetails.employee_name}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Resigned Date">
                                                {selectedDetails.resigned_date}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Requested LWD">
                                                {selectedDetails.requested_last_working_day}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Pending With">
                                                {selectedDetails.pending_with || "-"}
                                            </Descriptions.Item>
                                        </Descriptions>

                                        <Divider />
                                        <h6>Reason</h6>
                                        <br />
                                        <Paragraph style={{ fontSize: "18px", lineHeight: 1.8, whiteSpace: "pre-line", marginBottom: 0 }}>
                                            {selectedDetails.reason}
                                        </Paragraph>
                                        <br />
                                        <Divider />

                                        <Title level={5}>Approval Logs</Title>
                                        <Table
                                            dataSource={selectedDetails.approval_logs || []}
                                            rowKey={(row, i) => i}
                                            pagination={false}
                                            size="small"
                                            bordered
                                            columns={[
                                                { title: "Order", dataIndex: "order" },
                                                { title: "Approver", dataIndex: "approver_name" },
                                                {
                                                    title: "Status",
                                                    dataIndex: "status",
                                                    render: (s) => (
                                                        <Tag color={s === "APPROVED" ? "green" : s === "PENDING" ? "orange" : "red"}>
                                                            {s}
                                                        </Tag>
                                                    ),
                                                },
                                                { title: "Remarks", dataIndex: "remarks", render: (r) => r || "-" },
                                                {
                                                    title: "Action Time",
                                                    dataIndex: "action_timestamp",
                                                    render: (t) => t ? new Date(t).toLocaleString("en-GB") : "-",
                                                },
                                            ]}
                                        />
                                    </>
                                ),
                            },
                            selectedDetails.withdrawal && {
                                key: "withdrawal",
                                label: "Withdrawal",
                                children: (
                                    <>
                                        <Descriptions bordered column={2} style={{ marginBottom: 20 }}>
                                            <Descriptions.Item label="Employee">
                                                {selectedDetails.withdrawal.withdrawal_request?.employee?.user?.first_name || "-"}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Reason">
                                                {selectedDetails.withdrawal.withdrawal_request?.reason || "-"}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Status">
                                                <Tag color={selectedDetails.withdrawal.status === "APPROVED" ? "green" : "orange"}>
                                                    {selectedDetails.withdrawal.status}
                                                </Tag>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Created At">
                                                {selectedDetails.withdrawal.withdrawal_request?.created_at
                                                    ? new Date(selectedDetails.withdrawal.withdrawal_request.created_at).toLocaleString("en-GB")
                                                    : "-"}
                                            </Descriptions.Item>
                                        </Descriptions>

                                        <Title level={5}>Approval Logs</Title>
                                        <Table
                                            dataSource={selectedDetails.withdrawal.withdrawal_request?.approval_logs || []}
                                            rowKey={(row, i) => i}
                                            pagination={false}
                                            size="small"
                                            bordered
                                            columns={[
                                                { title: "Order", dataIndex: "order" },
                                                { title: "Approver", dataIndex: "approver_name" },
                                                {
                                                    title: "Status",
                                                    dataIndex: "status",
                                                    render: (s) => (
                                                        <Tag color={s === "APPROVED" ? "green" : s === "PENDING" ? "orange" : "red"}>
                                                            {s}
                                                        </Tag>
                                                    ),
                                                },
                                                { title: "Remarks", dataIndex: "remarks", render: (r) => r || "-" },
                                                {
                                                    title: "Action Time",
                                                    dataIndex: "action_timestamp",
                                                    render: (t) => t ? new Date(t).toLocaleString("en-GB") : "-",
                                                },
                                            ]}
                                        />
                                    </>
                                ),
                            },
                            selectedDetails.waiver && {
                                key: "waiver",
                                label: "Waiver",
                                children: (
                                    <>
                                        <Descriptions bordered column={2} style={{ marginBottom: 20 }}>
                                            <Descriptions.Item label="Employee">
                                                {selectedDetails.waiver.waiver_request?.employee?.user?.first_name || "-"}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Reason">
                                                {selectedDetails.waiver.waiver_request?.reason || "-"}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Status">
                                                <Tag color={selectedDetails.waiver.status === "APPROVED" ? "green" : "orange"}>
                                                    {selectedDetails.waiver.status}
                                                </Tag>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Created At">
                                                {selectedDetails.waiver.waiver_request?.created_at
                                                    ? new Date(selectedDetails.waiver.waiver_request.created_at).toLocaleString("en-GB")
                                                    : "-"}
                                            </Descriptions.Item>
                                        </Descriptions>




                                        <Title level={5}>Approval Logs</Title>
                                        <Table
                                            dataSource={selectedDetails.waiver.waiver_request?.approval_logs || []}
                                            rowKey={(row, i) => i}
                                            pagination={false}
                                            size="small"
                                            bordered
                                            columns={[
                                                { title: "Order", dataIndex: "order" },
                                                { title: "Approver", dataIndex: "approver_name" },
                                                {
                                                    title: "Status",
                                                    dataIndex: "status",
                                                    render: (s) => (
                                                        <Tag color={s === "APPROVED" ? "green" : s === "PENDING" ? "orange" : "red"}>
                                                            {s}
                                                        </Tag>
                                                    ),
                                                },
                                                { title: "Remarks", dataIndex: "remarks", render: (r) => r || "-" },
                                                {
                                                    title: "Action Time",
                                                    dataIndex: "action_timestamp",
                                                    render: (t) => t ? new Date(t).toLocaleString("en-GB") : "-",
                                                },
                                            ]}
                                        />
                                    </>
                                ),
                            },
                        ].filter(Boolean)}
                    />
                )}
            </Modal>

            <ToastContainer />
        </div>
    );
}